import { Injectable } from '@angular/core'
import { Actions, ofType, OnInitEffects, createEffect } from '@ngrx/effects'
import { AuthService } from 'src/app/common'
import * as UserActions from '../actions/user.actions'
import { map, switchMap, catchError, tap } from 'rxjs/operators'
import { of, Observable, from } from 'rxjs'
import { Router } from '@angular/router'
import { ToastrService } from 'ngx-toastr'
import { HttpErrorResponse } from '@angular/common/http'
import { JrpcService } from 'src/app/common/jrpc.service'
import { ErrorHandlerService } from 'src/app/common/error-handler.service'
import { CenterMeasurementUnitsService } from 'src/app/centers/shared/center.measurement-units.service'

@Injectable()
export class UserEffects implements OnInitEffects {
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.login),
      switchMap(({ email, password }) =>
        this.authService.login({ email, password }).pipe(
          switchMap(() => this.finishLogin()),
          map(() => UserActions.loginSuccess()),
          catchError((error) => {
            if (error instanceof HttpErrorResponse && error.error && error.error.message) {
              this.toastr.error(error.error.message)
            } else {
              const message = 'Some server error'
              this.errorHandlerService.showErrorMessage(error, message)
            }
            return of(UserActions.loginFailure({ error }))
          }),
        ),
      ),
    ),
  )

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.logout),
      switchMap(() =>
        this.authService.logout().pipe(
          tap(() => this.jrpc.destroyService()),
          switchMap(() => this.finishLogout()),
          map(() => UserActions.logoutSuccess()),
          catchError((error) => this.finishLogout().pipe(map(() => UserActions.logoutFailure({ error })))),
        ),
      ),
    ),
  )

  authenticate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.authenticate, UserActions.loginSuccess),
      switchMap(() =>
        this.authService.autheticate().pipe(
          map(({ user, permissions }) => {
            if (user) {
              this.jrpc.initService()
              this.centerMeasurementUnitsService.loadMeasurmentUnits()
              return UserActions.authenticateSuccess({ user, permissions })
            } else {
              return UserActions.authenticateFailure({ error: { message: 'Not authenticated' } })
            }
          }),
          catchError((error) => of(UserActions.authenticateFailure({ error }))),
        ),
      ),
    ),
  )

  changeAvatar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.changeAvatar),
      switchMap((action) =>
        this.authService.setUserAvatar(action.file).pipe(
          map((url) => UserActions.changeAvatarSuccess({ url })),
          catchError((error) => of(UserActions.changeAvatarFailure({ error }))),
        ),
      ),
    ),
  )

  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private toastr: ToastrService,
    private router: Router,
    private jrpc: JrpcService,
    private errorHandlerService: ErrorHandlerService,
    private centerMeasurementUnitsService: CenterMeasurementUnitsService,
  ) {}

  finishLogout(): Observable<boolean> {
    return from(this.router.navigate(['/login']))
  }

  finishLogin(): Observable<boolean> {
    return from(this.router.navigate(['app/dashboard']))
  }

  ngrxOnInitEffects() {
    return UserActions.authenticate()
  }
}
